import React from 'react';
import Actions from 'components/ui/general/Actions';
import Button from 'components/button/ActionButton';
import Breadcrumbs from 'components/layout/Layout/Breadcrumbs';
import compose from 'recompose/compose';
import ArrayConfiguration from './ArrayConfiguration';
import DateTableCell from './DateTableCell';
import fetchResourceById from 'components/hoc/fetchResourceById';
import Header from 'components/ui/general/Header';
import LoadingCard from 'components/loading/LoadingCard';
import MultiFieldConfiguration from './MultiFieldConfiguration';
import PropTypes from 'prop-types';
import ResourceNotFound from 'pages/NotFound/ResourceNotFound';
import SingleFieldConfiguration from './SingleFieldConfiguration';
import TextInputTableCell from './TextInputTableCell';
import theme from 'theme/theme';
import TimeTableCell from './TimeTableCell';
import Title from 'components/ui/general/Title';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';
import { connect } from 'react-redux';
import { fetchProduct } from 'store/actions/products/fetchProducts';
import { saveProductConfiguration } from 'store/actions/products/configuration/saveConfiguration';
import { SaveIcon } from 'assets/materialIcons/materialIcons';
import PercentageInputField from 'components/form/InputFields/PercentageInputField';
import ProductRulesConfigurationContainer from './rules/ProductRulesConfigurationContainer';
import SelectInputTableCell from './SelectInputTableCell';
import { each, forEach, isBoolean, isEmpty, omit, range } from 'lodash';
import ObjectConfiguration from './ObjectConfiguration';
import BooleanSelectField from 'components/input/SelectField/BooleanSelectField';
import ChoiceSelectField from 'components/input/SelectField/ChoiceSelectField';

const createRangeObject = (range) => {
    return {
        id: Math.floor(Math.random() * Date.now()),
        ...range,
    };
};

const formatRangeObjects = (ranges) => {
    return ranges.map(({ from, to, weekday = null }) => {
        return createRangeObject({
            from,
            to,
            weekday,
        });
    });
};

const formatKeyValueRangeObjects = (ranges) => {
    return utils.map(ranges, (value = null, key = null) => {
        return createRangeObject({
            key,
            value,
        });
    });
};

const formatPercentage = (value) => {
    return utils.isNumber(value) ? parseFloat(value * 100) : 0;
};

const loadStateValues = (configuration) => {
    // Backward compatability as a string
    let terms_and_conditions = configuration.terms_and_conditions;

    if (typeof terms_and_conditions === 'string') {
        terms_and_conditions = JSON.parse(terms_and_conditions);
    }

    return {
        administration_fee: configuration.administration_fee || null,
        allow_exceed_capacity_from: configuration.allow_exceed_capacity_from || '',
        allow_overstay: configuration.allow_overstay || null,
        arrival_dates_blocked: configuration.arrival_dates_blocked ? formatRangeObjects(configuration.arrival_dates_blocked) : null,
        blocked_dates: configuration.blocked_dates ? formatRangeObjects(configuration.blocked_dates) : null,
        capacity: utils.has(configuration, 'capacity') ? configuration.capacity : '',
        communicate_email_addresses: utils.has(configuration, 'communicate_email_addresses') ? configuration.communicate_email_addresses : '',
        departure_dates_blocked: configuration.departure_dates_blocked ? formatRangeObjects(configuration.departure_dates_blocked) : null,
        freeze_period: configuration.freeze_period || '',
        leeway_arrival: configuration.leeway_arrival || '',
        leeway_departure: configuration.leeway_departure || '',
        maximum_advance: configuration.maximum_advance || '',
        maximum_duration: configuration.maximum_duration || '',
        minimum_advance: configuration.minimum_advance || '',
        minimum_duration: configuration.minimum_duration || '',
        number_of_persons: configuration.number_of_persons || '',
        operational_times: configuration.operational_times ? formatRangeObjects(configuration.operational_times) : null,
        rules: configuration.rules || null,
        sales_price: configuration.sales_price ? formatKeyValueRangeObjects(configuration.sales_price) : null,
        terms_and_conditions: terms_and_conditions,
        vat_percentage: formatPercentage(configuration.vat_percentage),
        voucher_include_invoice: configuration.voucher_include_invoice || null,
    };
};

class ProductConfigurationContainer extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            administration_fee: null,
            allow_exceed_capacity_from: '',
            allow_overstay: null, // Boolean
            arrival_dates_blocked: null,
            blocked_dates: null,
            capacity: '',
            departure_dates_blocked: null,
            freeze_period: '',
            internal_qr_code: null, // Boolean
            leeway_arrival: '',
            leeway_departure: '',
            maximum_advance: '',
            maximum_duration: '',
            minimum_advance: '',
            minimum_duration: '',
            number_of_persons: '',
            operational_times: null,
            rules: null,
            sales_price: null,
            terms_and_conditions: null,
            vat_percentage: 0,
            voucher_include_invoice: null, // Boolean
            split_percentage: 0,
            errors: {},
        };
    }

    componentDidMount() {
        const { data, resourceFetchLoading } = this.props;

        if (!resourceFetchLoading && !!data.configuration) {
            this.setState(loadStateValues(data.configuration));
        }
    }

    componentDidUpdate(prevProps) {
        const { data } = this.props;

        if (prevProps.data !== data) {
            if (data.configuration) {
                this.setState(loadStateValues(data.configuration));
            }
        }
    }

    getKeyValueObjectFromState = (key) => {
        const keyValueState = this.state[key] || [];

        const formattedEntries = keyValueState
            .map((entries) => {
                const formattedEntries = utils.filterEmptyValues(omit(entries, [
                    'id',
                ]));

                return isEmpty(formattedEntries) ? null : formattedEntries;
            }).filter((entry) => {
                return entry.key && entry.value;
            });

        const result = {};

        each(formattedEntries, ({ key, value }) => {
            result[key] = value;
        });

        return isEmpty(result) ? null : result;
    }

    getRangeObjectsFromState = (key) => {
        const rangeState = this.state[key] || [];

        const formattedRanges = rangeState
            .map((range) => {
                const formattedRange = utils.filterEmptyValues(omit(range, [
                    'id',
                ]));

                return isEmpty(formattedRange) ? null : formattedRange;
            }).filter((range) => {
                return range;
            });

        return isEmpty(formattedRanges) ? null : formattedRanges;
    }

    getTextInputValue = (value) => {
        return value || null;
    }

    getObjectInputValue = (objectValues) => {
        const inputValue = {};

        forEach(objectValues, (value, key) => {
            if (value) {
                inputValue[key] = value;
            }
        });

        return isEmpty(inputValue) ? null : inputValue;
    }

    parsePercentage = (value) => {
        return this.getTextInputValue(value) ? parseFloat(value / 100).toFixed(2) : null;
    }

    onSaveProductConfiguration = () => {
        const {
            administration_fee,
            allow_exceed_capacity_from,
            allow_overstay,
            capacity,
            communicate_email_addresses,
            freeze_period,
            internal_qr_code,
            leeway_arrival,
            leeway_departure,
            maximum_advance,
            maximum_duration,
            minimum_advance,
            minimum_duration,
            number_of_persons,
            rules,
            vat_percentage,
            split_percentage,
            terms_and_conditions,
            voucher_include_invoice,
        } = this.state;

        const { data, dispatchProductConfiguration, id, translate } = this.props;

        return new Promise((resolve) => {
            dispatchProductConfiguration({
                onError: ({ response }) => {
                    const errors = {};

                    each(response.errors, (errorMessages, key) => {
                        const fieldKey = key.split('.')[1];

                        errors[fieldKey] = translate(`notifications.products.configuration.${ fieldKey }`);
                    });

                    this.setState({
                        errors: errors,
                    });

                    resolve();
                },
                onSuccess: () => {},
                configuration: utils.normalizePayload({
                    ...data.configuration,
                    administration_fee: this.getObjectInputValue(administration_fee),
                    allow_exceed_capacity_from: this.getTextInputValue(allow_exceed_capacity_from),
                    allow_overstay: isBoolean(allow_overstay) ? allow_overstay : null,
                    arrival_dates_blocked: this.getRangeObjectsFromState('arrival_dates_blocked'),
                    blocked_dates: this.getRangeObjectsFromState('blocked_dates'),
                    capacity: this.getTextInputValue(capacity),
                    communicate_email_addresses: this.getTextInputValue(communicate_email_addresses),
                    departure_dates_blocked: this.getRangeObjectsFromState('departure_dates_blocked'),
                    freeze_period: this.getTextInputValue(freeze_period),
                    internal_qr_code: isBoolean(internal_qr_code) ? internal_qr_code : null,
                    leeway_arrival: this.getTextInputValue(leeway_arrival),
                    leeway_departure: this.getTextInputValue(leeway_departure),
                    maximum_advance: this.getTextInputValue(maximum_advance),
                    maximum_duration: this.getTextInputValue(maximum_duration),
                    minimum_advance: this.getTextInputValue(minimum_advance),
                    minimum_duration: this.getTextInputValue(minimum_duration),
                    number_of_persons: this.getTextInputValue(number_of_persons),
                    operational_times: this.getRangeObjectsFromState('operational_times'),
                    rules: rules,
                    sales_price: this.getKeyValueObjectFromState('sales_price'),
                    terms_and_conditions,
                    vat_percentage: this.parsePercentage(vat_percentage),
                    split_percentage: this.parsePercentage(split_percentage),
                    voucher_include_invoice: isBoolean(voucher_include_invoice) ? voucher_include_invoice : null,
                }),
                id: id,
            });
        });
    };

    onAddRangeByKey = (key) => {
        const rangeState = this.state[key];
        const newRangeObject = createRangeObject('', '');

        this.onChange(key, [
            ...rangeState || [],
            newRangeObject,
        ]);
    }

    onAddKeyValueRangeByKey = (key) => {
        const rangeState = this.state[key];

        this.onChange(key, [
            ...rangeState || [],
            {
                id: Math.floor(Math.random() * Date.now()),
                key: '',
                value: '',
            },
        ]);
    }

    onChange = (key, value = null) => {
        this.setState({
            [key]: value,
            errors: {},
        });
    }

    onChangeObject = (attribute, key, value = null) => {
        this.setState({
            [attribute]: {
                ...(this.state[attribute] || {}),
                [key]: value,
            },
            errors: {},
        });
    }

    onChangeRangeByKey = (event, rangeObject, key) => {
        const rangeState = this.state[key];

        const value = event.target.value;
        const name = event.target.name;
        const { id } = rangeObject;

        const updatedRangeObject = rangeState.map((range) => {
            if (range.id === id) {
                return { ...range, [name]: value };
            }

            return range;
        });

        this.onChange(key, updatedRangeObject);
    }

    onDeleteRangeByKey = (id, key) => {
        const rangeState = this.state[key];

        const updatedRange = rangeState.filter((range) => {
            return range.id !== id;
        });

        this.onChange(key, updatedRange);
    }

    onChangeTextInput = (e) => {
        const { name, value } = e.target;

        this.onChange(name, value);
    }

    render() {
        const {
            id,
            data,
            resourceFetchFailed,
            resourceFetchLoading,
            translate,
        } = this.props;

        const {
            administration_fee,
            allow_exceed_capacity_from,
            allow_overstay,
            arrival_dates_blocked,
            blocked_dates,
            capacity,
            communicate_email_addresses,
            departure_dates_blocked,
            errors,
            freeze_period,
            internal_qr_code,
            leeway_arrival,
            leeway_departure,
            maximum_advance,
            maximum_duration,
            minimum_advance,
            minimum_duration,
            number_of_persons,
            operational_times,
            rules,
            sales_price,
            split_percentage,
            terms_and_conditions,
            vat_percentage,
            voucher_include_invoice,
        } = this.state;

        if (resourceFetchLoading) {
            return <LoadingCard />;
        }

        if (resourceFetchFailed) {
            return <ResourceNotFound />;
        }

        const breadcrumbs = [
            {
                label: translate('resources.products.title'),
                link: '/products',
            },
            {
                label: data.title,
                link: `/products/${ id }/show`,
            },
            {
                label: translate('resources.products.fields.configuration'),
            },
        ];

        const salesPriceFieldInputWrapperStyle = {
            'value': {
                width: '100%',
            },
        };

        return (
            <div>
                <Breadcrumbs breadcrumbs={ breadcrumbs } />

                <Header>
                    <Title title={ data.title } />

                    <Actions>
                        <Button
                            icon={ <SaveIcon color={ theme.palette.gray }/> }
                            title={ translate('actions.save') }
                            onClick={ this.onSaveProductConfiguration }
                            primary={ false }
                        />
                    </Actions>
                </Header>

                <SingleFieldConfiguration
                    component={ PercentageInputField }
                    errors={ errors }
                    hintText={ translate('resources.products.vat_percentage.placeholder') }
                    label={ translate('resources.products.fields.vat_percentage') }
                    name='vat_percentage'
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.fields.vat_percentage') }
                    value={ vat_percentage }
                />

                <SingleFieldConfiguration
                    component={ PercentageInputField }
                    errors={ errors }
                    hintText={ translate('resources.products.split_percentage.placeholder') }
                    label={ translate('resources.products.fields.split_percentage') }
                    name='split_percentage'
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.fields.split_percentage') }
                    value={ split_percentage }
                />

                <ObjectConfiguration
                    component={ TextInputTableCell }
                    errors={ errors }
                    fields={ [
                        'minimum',
                        'percentage',
                        'maximum',
                    ] }
                    id='config-administration-fee'
                    name='administration_fee'
                    onChange={ this.onChangeObject }
                    values={ Object.assign({}, (this.props.data.configuration || {}).administration_fee, administration_fee) }
                    title={ translate('resources.products.administration_fee.title') }
                />

                <ArrayConfiguration
                    component={ TimeTableCell }
                    components={ {
                        weekday: <SelectInputTableCell choices={ range(0, 8).map((value) => {
                            return {
                                text: value ? translate(`fields.weekday.${ value }`) : '',
                                value: value || null,
                            };
                        }) } />,
                    } }
                    errors={ errors }
                    fields={ [
                        'from',
                        'to',
                        'weekday',
                    ] }
                    id='table-operational-times'
                    name='operational_times'
                    onAdd={ this.onAddRangeByKey }
                    onChange={ this.onChangeRangeByKey }
                    onDelete={ this.onDeleteRangeByKey }
                    values={ operational_times }
                    title={ translate('resources.products.operational_times.title') }
                />

                <ArrayConfiguration
                    component={ DateTableCell }
                    errors={ errors }
                    id='table-blocked-dates'
                    name='blocked_dates'
                    onAdd={ this.onAddRangeByKey }
                    onChange={ this.onChangeRangeByKey }
                    onDelete={ this.onDeleteRangeByKey }
                    values={ blocked_dates }
                    title={ translate('resources.products.blocked_dates.title') }
                />

                <ArrayConfiguration
                    component={ DateTableCell }
                    errors={ errors }
                    id='table-blocked-arrival'
                    name='arrival_dates_blocked'
                    onAdd={ this.onAddRangeByKey }
                    onChange={ this.onChangeRangeByKey }
                    onDelete={ this.onDeleteRangeByKey }
                    values={ arrival_dates_blocked }
                    title={ translate('resources.products.arrival_dates_blocked.title') }
                />

                <ArrayConfiguration
                    component={ DateTableCell }
                    errors={ errors }
                    id='table-blocked-departure'
                    name='departure_dates_blocked'
                    onAdd={ this.onAddRangeByKey }
                    onChange={ this.onChangeRangeByKey }
                    onDelete={ this.onDeleteRangeByKey }
                    values={ departure_dates_blocked }
                    title={ translate('resources.products.departure_dates_blocked.title') }
                />

                <ArrayConfiguration
                    component={ TextInputTableCell }
                    errors={ errors }
                    id='table-sales-price'
                    fields={ [
                        'key',
                        'value',
                    ] }
                    fieldInputWrapperStyles={ salesPriceFieldInputWrapperStyle }
                    name='sales_price'
                    onAdd={ this.onAddKeyValueRangeByKey }
                    onChange={ this.onChangeRangeByKey }
                    onDelete={ this.onDeleteRangeByKey }
                    values={ sales_price }
                    title={ translate('resources.products.sales_price.title') }
                />

                <MultiFieldConfiguration
                    errors={ errors }
                    hintText={ translate('resources.products.duration.placeholders.duration') }
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.fields.duration') }
                    translationKey='resources.products.duration.fields'
                    values={ {
                        maximum_duration,
                        minimum_duration,
                    } }
                />

                <SingleFieldConfiguration
                    errors={ errors }
                    label={ translate('resources.products.capacity.title') }
                    name='capacity'
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.capacity.title') }
                    value={ capacity }
                />

                <SingleFieldConfiguration
                    errors={ errors }
                    hintText={ translate('resources.products.duration.placeholders.duration') }
                    label={ translate('resources.products.allowed_capacity_excess.fields.capacity_excess') }
                    name='allow_exceed_capacity_from'
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.allowed_capacity_excess.title') }
                    value={ allow_exceed_capacity_from }
                />

                <MultiFieldConfiguration
                    errors={ errors }
                    hintText={ translate('resources.products.leeway.placeholders.leeway') }
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.fields.leeway') }
                    translationKey='resources.products.leeway.fields'
                    values={ {
                        leeway_arrival,
                        leeway_departure,
                    } }
                />

                <MultiFieldConfiguration
                    errors={ errors }
                    hintText={ translate('resources.products.duration.placeholders.duration') }
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.advancedTimes.title') }
                    translationKey='resources.products.advancedTimes.fields'
                    values={ {
                        maximum_advance,
                        minimum_advance,
                    } }
                />

                <SingleFieldConfiguration
                    errors={ errors }
                    hintText={ translate('resources.products.number_of_persons.placeholder') }
                    label={ translate('resources.products.fields.number_of_persons') }
                    name='number_of_persons'
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.fields.number_of_persons') }
                    value={ number_of_persons }
                />

                <SingleFieldConfiguration
                    errors={ errors }
                    hintText={ translate('resources.products.freeze_period.placeholder') }
                    label={ translate('resources.products.fields.freeze_period') }
                    name='freeze_period'
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.fields.freeze_period') }
                    value={ freeze_period }
                />

                <SingleFieldConfiguration
                    component={ BooleanSelectField }
                    errors={ errors }
                    hintText={ translate('resources.products.allow_overstay.placeholder') }
                    label={ translate('resources.products.fields.allow_overstay') }
                    name='allow_overstay'
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.fields.allow_overstay') }
                    value={ allow_overstay }
                />

                <SingleFieldConfiguration
                    component={ BooleanSelectField }
                    errors={ errors }
                    hintText={ translate('resources.products.voucher_include_invoice.placeholder') }
                    label={ translate('resources.products.fields.voucher_include_invoice') }
                    name='voucher_include_invoice'
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.fields.voucher_include_invoice') }
                    value={ voucher_include_invoice }
                />

                <SingleFieldConfiguration
                    component={ BooleanSelectField }
                    errors={ errors }
                    hintText={ translate('resources.products.internal_qr_code.placeholder') }
                    label={ translate('resources.products.fields.internal_qr_code') }
                    name='internal_qr_code'
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.fields.internal_qr_code') }
                    value={ internal_qr_code }
                />

                <SingleFieldConfiguration
                    choices={ [
                        {
                            label: translate('resources.products.communicate_email_addresses.choices.do_not_send'),
                            value: 'do_not_send',
                        },
                        {
                            label: translate('resources.products.communicate_email_addresses.choices.send'),
                            value: 'send',
                        },
                        {
                            label: translate('resources.products.communicate_email_addresses.choices.send_masked'),
                            value: 'send_masked',
                        },
                    ] }
                    component={ ChoiceSelectField }
                    errors={ errors }
                    hintText={ translate('resources.products.communicate_email_addresses.placeholder') }
                    label={ translate('resources.products.fields.communicate_email_addresses') }
                    name='communicate_email_addresses'
                    onChangeInput={ this.onChangeTextInput }
                    title={ translate('resources.products.fields.communicate_email_addresses') }
                    value={ communicate_email_addresses }
                />

                <ObjectConfiguration
                    component={ TextInputTableCell }
                    errors={ errors }
                    fields={ [
                        'name',
                        'link',
                    ] }
                    id='terms_and_conditions'
                    name='terms_and_conditions'
                    onChange={ this.onChangeObject }
                    values={ Object.assign({}, (this.props.data.configuration || {}).terms_and_conditions, terms_and_conditions) }
                    title={ translate('resources.products.fields.terms_and_conditions.title') }
                />

                <ProductRulesConfigurationContainer
                    productId={ id }
                    name='rules'
                    onSubmitChange={ this.onChange }
                    rules={ rules }
                />

            </div>
        );
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        fetch: (values) => {
            dispatch(fetchProduct(values));
        },
        dispatchProductConfiguration: (values) => {
            dispatch(saveProductConfiguration(values));
        },
    };
};

const enhance = compose(
    translate,
    connect(
        null,
        mapDispatchToProps,
    ),
    fetchResourceById({
        action: fetchProduct,
        resource: 'products',
    })
);

ProductConfigurationContainer.propTypes = {
    data: PropTypes.object,
    dispatchProductConfiguration: PropTypes.func,
    id: PropTypes.string,
    resourceFetchFailed: PropTypes.bool.isRequired,
    resourceFetchLoading: PropTypes.bool.isRequired,
    translate: PropTypes.func.isRequired,
};

export default enhance(ProductConfigurationContainer);



// WEBPACK FOOTER //
// ./src/pages/products/configuration/ProductConfigurationContainer.js